<template>
  <esg-base-chart height="300px">
    <loading :active="loading"
             :z-index="1000"
             :is-full-page="false"/>
    <apex-chart ref="chart"
                v-show="!loading"
                type="bar"
                :width=this.width
                :height=this.height
                :options="chartOptions"
                :series="series"/>
  </esg-base-chart>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";

export default {
  components: { EsgBaseChart },
  props: {
    businessLocation: {
      type: Object,
      required: true
    },
    width: {
      type: String,
      default: '100%',
    },
    height: {
      type: String,
      default: '300px',
    },
    sort: {
      type: String,
      default: 'asc'
    },
    colors: {
      type: Array,
      default() {
        return ['#333']
      }
    }
  },
  data() {
    return {
      loading: false,
      fuels: [],
      ascCategories: [],
      descCategories: [],
      ascSeries: [],
      descSeries: [],
      series: [],
      chartOptions: {
        colors: this.colors,
        chart: {
          toolbar: {
            show: false
          }
        },
        plotOptions: {
          bar: {
            horizontal: true,
            fontFamily: 'NanumGothic',
          }
        },
        dataLabels: {
          enabled: false,
        },
        stroke: {
          show: true,
          width: 1,
          colors: ['#fff']
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: (val, opt) => {
              const dataPointIndex = opt.dataPointIndex;
              const seriesIndex = opt.seriesIndex;
              const dataPoint = opt.series[seriesIndex][dataPointIndex];
              const total = opt.series[seriesIndex].reduce((a, b) => a + b, 0);
              const share = 100 * dataPoint / total;
              return `${this.formatNumber(dataPoint, 2)} TJ (${this.formatNumber(share, 2)}%)`
            }
          }
        },
        xaxis: {
          categories: [],
          labels: {
            formatter: val => this.compactFormatNumber(val)
          },
        },
        legend: {
          position: 'right'
        },
        title: {
          text: `${this.businessLocation.name}`
        },
      }
    }
  },
  watch: {
    corporation: {
      deep: true,
      immediate: true,
      handler() {
        this.fetchData();
      }
    },
    sort: {
      immediate: true,
      handler(sort) {
        this.sortSeries(sort);
      }
    }
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    async fetchData() {
      if (!this.businessLocation ||
          !this.businessLocation.corporation_code ||
          !this.businessLocation.business_location_code) return;

      try {
        this.loading = true;

        let year = new Date().getFullYear();
        let years = [year];
        let args = [{
          years,
          corporations: [this.businessLocation.corporation_code],
          businessLocations: [this.businessLocation.business_location_code],
          groupKeys: ['year', 'fuel_code', 'fuel_name']
        }];

        let data = new FormData()
        data.append('path', 'esg/energy_usage/main');
        data.append('json', JSON.stringify(args));

        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });

        if (!response.data.data) {
          this.series = [];
          return;
        }
        ;

        let list = JSON.parse(response.data.data)[0].data;

        // Extract fuel names
        this.fuels = list.map(row => row.fuel_name);

        // Extract energy amounts
        let energy = this.fuels.map(fuel =>
            list.filter(row => row.fuel_name === fuel)
                .map(row => row.values)
                .reduce((a, b) => a + b, 0)
        );

        // Sort fuels based on the totals in descending order
        this.descCategories = this.fuels.slice();
        this.descCategories.sort((a, b) => {
          let indA = this.fuels.findIndex(fuel => fuel === a);
          let indB = this.fuels.findIndex(fuel => fuel === b);
          return energy[indB] - energy[indA];
        });

        // Reverse the categories sorted in descending order
        this.ascCategories = this.descCategories.slice();
        this.ascCategories.reverse();

        // Construct series
        this.descSeries = [];
        this.ascSeries = [];
        let descData = this.descCategories.map(fuel =>
            list.find(row => row.fuel_name === fuel).values
        );
        let ascData = descData.slice();
        ascData.reverse();

        this.descSeries.push({
          name: year,
          data: descData.slice(0, 10)
        });
        this.ascSeries.push({
          name: year,
          data: ascData.slice(0, 10)
        });

        // Slice categories
        this.ascCategories = this.ascCategories.slice(0, 10);
        this.descCategories = this.descCategories.slice(0, 10);

        // Select data set according to the sorting order
        this.sortSeries(this.sort);
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    sortSeries(sort) {
      if (sort === 'desc') {
        this.series = this.descSeries
        this.chartOptions.xaxis.categories = this.descCategories;
      } else {
        this.series = this.ascSeries;
        this.chartOptions.xaxis.categories = this.ascCategories;
      }
      this.refreshChart();
    }
  }
}
</script>
<style>
</style>